import { NgStyle } from '@angular/common'
import { Component, ViewEncapsulation } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faPaperPlane, faPlus, faSave, faSpinner, faTrash, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import {
    FeatureType,
    IiEmployee,
    IiWitness,
    IncidentInvestigation,
    ListItem,
    RepositoryModal,
} from '../../shared'
import { VoiceTextFieldComponent } from '../../shared/components/voice-text-field/voice-text-field.component'
import { IncidentInvestigationListComponent } from './incident-investigation-list.component'

function newGuid(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
              const r = (Math.random() * 16) | 0
              const v = c === 'x' ? r : (r & 0x3) | 0x8
              return v.toString(16)
          })
}

@Component({
    templateUrl: './incident-investigation-modal.component.html',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle, VoiceTextFieldComponent],
    styles: [
        `
            .ii-modal-footer {
                margin-top: 0 !important;
            }
        `,
    ],
})
export class IncidentInvestigationModalComponent extends RepositoryModal<IncidentInvestigation> {
    faX = faX
    faSpinner = faSpinner
    faSave = faSave
    faPaperPlane = faPaperPlane
    faPlus = faPlus
    faTrash = faTrash

    userList: ListItem<string>[] = []
    jobList: ListItem<string>[] = []
    incidentTypeList: ListItem<string>[] = [
        new ListItem('Near Miss', 'Near Miss'),
        new ListItem('Injury', 'Injury'),
        new ListItem('Accident', 'Accident'),
        new ListItem('Other', 'Other'),
    ]

    images: any[] = []
    progress = 0

    private submitOnSave = false

    protected onInitialized(): void {
        const parent = this.editOptions.initializeParams as IncidentInvestigationListComponent
        this.userList = parent.activeUserList ?? []
        this.jobList = parent.activeJobList ?? []

        if (!this.target.employees) {
            this.target.employees = []
        }
        if (!this.target.witnesses) {
            this.target.witnesses = []
        }

        if (this.isAdding && !this.target.reportingPartyUserId) {
            const currentUserId = parent.currentUserId
            if (currentUserId && this.userList.some(u => u.value === currentUserId)) {
                this.target.reportingPartyUserId = currentUserId
            }
        }
    }

    get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyIncidentInvestigationEdit])
    }

    addEmployee() {
        const employee = new IiEmployee()
        employee.id = newGuid()
        employee.sortOrder = this.target.employees.length
        this.target.employees.push(employee)
    }

    removeEmployee(index: number) {
        this.target.employees.splice(index, 1)
        this.target.employees.forEach((e, i) => (e.sortOrder = i))
    }

    addWitness() {
        const witness = new IiWitness()
        witness.id = newGuid()
        witness.sortOrder = this.target.witnesses.length
        this.target.witnesses.push(witness)
    }

    removeWitness(index: number) {
        this.target.witnesses.splice(index, 1)
        this.target.witnesses.forEach((w, i) => (w.sortOrder = i))
    }

    removeImage(image: any) {
        this.images.remove(image)
    }

    async onFileChanged(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files) return

        const progress = (p: number) => {
            this.progress = p
        }

        for (const file of Array.from(files)) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }

        input.value = ''
    }

    getExistingImageSrc(url: string): string {
        return './Resources/Images/IncidentInvestigation/' + url + '?w=300'
    }

    openExistingImage(url: string) {
        window.open('./Resources/Images/IncidentInvestigation/' + url, '_blank')
    }

    displayFileName(index: number): string {
        return this.target.imageFileNames?.[index] ?? 'Photo'
    }

    saveDraft() {
        this.submitOnSave = false
        if (this.target.status !== 'Submitted') {
            this.target.status = 'Draft'
        }
        this.save()
    }

    submit() {
        this.submitOnSave = true
        this.target.status = 'Submitted'
        if (!this.target.submittedOn) {
            this.target.submittedOn = new Date()
        }
        this.save()
    }

    protected override onBeforeSave(): boolean {
        this.target.employees = (this.target.employees ?? []).filter(e => !!e.userId)
        this.target.witnesses = this.target.witnesses ?? []
        return true
    }

    private uploadImages(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.images.length; i++) {
            uploadData.append('myFile' + i, this.images[i], this.images[i].name)
        }
        return this.services.data.post('api/IncidentInvestigation/UploadImages', uploadData)
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.images.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadImages(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }
}
